import { observer } from 'mobx-react-lite'
import { Box } from '@mantine/core'
import type { DiscoverUserDto } from '../../app/models/discover'
import { Profile } from '../../app/models/profile'
import {
  AmmoScoreBadge,
  AppButton,
  AppText,
  ProfileUserAvatar
} from '../../app/common/ui'

interface ProfileCardProps {
  profile: Profile;
}

/** Compact discover-row preview (popover, links, etc.). */
export default observer(function ProfileCard ({ profile }: ProfileCardProps) {
  const followersCount = profile.followersCount ?? 0

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <ProfileUserAvatar
        variant="discover"
        size={52}
        imageUrl={profile.image}
        displayName={profile.displayName}
        username={profile.username}
        tier={profile.ammoTier}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AppText weight={600} size="sm" lineClamp={1}>
          {profile.displayName || profile.username}
        </AppText>
        <AppText size="xs" color="dimmed">
          @{profile.username}
        </AppText>
        {profile.bio && (
          <AppText size="sm" color="dimmed" lineClamp={2} mt={4}>
            {profile.bio}
          </AppText>
        )}
        <AppText size="xs" color="dimmed" mt="xs">
          {followersCount} follower{followersCount !== 1 ? 's' : ''}
        </AppText>
        {profile.ammoTier != null && (
          <Box mt={6}>
            <AmmoScoreBadge tier={profile.ammoTier} score={profile.ammoScore ?? 0} size="sm" />
          </Box>
        )}
      </Box>
    </Box>
  )
})

export interface ProfileDiscoverCardProps {
  user: DiscoverUserDto;
  isFollowing: boolean;
  onFollowToggle: (username: string) => void;
  onCardClick: (username: string) => void;
  isLoggedIn: boolean;
}

/** Discover grid cell: avatar row + optional follow CTA. */
export function ProfileDiscoverCard ({
  user,
  isFollowing,
  onFollowToggle,
  onCardClick,
  isLoggedIn
}: ProfileDiscoverCardProps) {
  return (
    <Box
      sx={{
        background: 'var(--gs-surface2)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }}
    >
      <Box
        onClick={() => onCardClick(user.username)}
        sx={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
      >
        <ProfileUserAvatar
          variant="discover"
          size={48}
          imageUrl={user.mainPhotoUrl}
          displayName={user.displayName}
          username={user.username}
          tier={user.ammoTier}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AppText
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--gs-text)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {user.displayName || user.username}
          </AppText>
          <AppText
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 11,
              color: 'var(--gs-muted)',
              marginBottom: 'var(--gs-xs)'
            }}
          >
            @{user.username}
          </AppText>
          <AmmoScoreBadge tier={user.ammoTier} score={user.ammoScore} size="sm" />
        </Box>
      </Box>

      {isLoggedIn && (
        <AppButton
          fullWidth
          size="xs"
          variant={isFollowing ? 'outline' : 'filled'}
          color={isFollowing ? 'gray' : 'orange'}
          onClick={() => onFollowToggle(user.username)}
          sx={isFollowing
            ? { borderColor: 'var(--gs-border-bright)' }
            : { background: 'var(--gs-accent)', '&:hover': { background: '#bf4a00' } }}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </AppButton>
      )}
    </Box>
  )
}
